import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthProvider";
import {
  Building2,
  Briefcase,
  Euro,
  TrendingUp,
  Target,
  ClipboardCheck,
  X,
  FileText,
  ShieldAlert,
  CheckCircle2,
  Users
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "../../../components/ui/Input";
import CustomSelect from "../../../components/ui/CustomSelect";
import { Button } from "../../../components/ui/button";
import api from "../../../api";
import { useNotification } from "../../../context/NotificationContext";

export default function SocieteAudit({ societe, open, onClose, onSave }) {
  const { showToast } = useNotification();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  
  const getInitialState = () => ({
    raisonSociale: "",
    denomination: "",
    siren: "",
    siret: "",
    formeJuridique: "",
    statutJuridique: "",
    secteurActivite: "",
    dateCreation: "",
    capitalSocial: "",
    pays: "France",
    adresseSiege: "",
    codePostal: "",
    ville: "",
    email: "",
    telephone: "",
    siteWeb: "",
    
    chiffreAffaires: "",
    ebitda: "",
    resultatNet: "",
    tresorerieDisponible: "",
    placementsEntreprise: "",
    valorisationEstimee: "",
    dettes: "",
    effectif: "",
    
    holding: false,
    
    nomDirigeant: "",
    prenomDirigeant: "",
    roleDirigeant: "",
    remunerationDirigeant: "",
    dividendes: "",
    compteCourantAssocie: "",
    pourcentageCapitalDirigeant: "",
    pourcentageVotesDirigeant: "",
    emailDirigeant: "",
    telephoneDirigeant: "",
    statutSocialDirigeant: "",
    
    besoinFinancement: "",
    projetFinancement: "",
    projetCession: false,
    horizonCession: "",
    transmissionEntreprise: false,
    croissanceRecherchee: false,
    leveeFonds: false,
    acquisition: false,
    
    forces: "",
    faiblesses: "",
    opportunites: "",
    risques: "",
    recommandations: "",
    commentairesConseiller: "",
    
    scoreComplexite: "",
    scoreFinancement: "",
    scoreTransmission: "",
    scoreRisque: "",
    
    dateAudit: new Date().toISOString().split("T")[0],
    auditeur: user?.Nom || "Non assigné"
  });

  const [formData, setFormData] = useState(getInitialState());

  useEffect(() => {
    if (open) {
      if (societe) {
        setFormData({
          ...getInitialState(),
          ...societe,
          denomination: societe.denomination || societe.raisonSociale || "",
          capitalSocial: societe.capitalSocial != null ? societe.capitalSocial : "",
          chiffreAffaires: societe.chiffreAffaires != null ? societe.chiffreAffaires : "",
          ebitda: societe.ebitda != null ? societe.ebitda : "",
          resultatNet: societe.resultatNet != null ? societe.resultatNet : "",
          tresorerieDisponible: societe.tresorerieDisponible != null ? societe.tresorerieDisponible : "",
          placementsEntreprise: societe.placementsEntreprise != null ? societe.placementsEntreprise : "",
          valorisationEstimee: societe.valorisationEstimee != null ? societe.valorisationEstimee : "",
          dettes: societe.dettes != null ? societe.dettes : "",
          effectif: societe.effectif != null ? societe.effectif : "",
          holding: Boolean(societe.holding),
          nomDirigeant: societe.gerant?.nom || societe.nomDirigeant || "",
          prenomDirigeant: societe.gerant?.prenom || societe.prenomDirigeant || "",
          roleDirigeant: societe.gerant?.role || societe.roleDirigeant || "",
          remunerationDirigeant: societe.gerant?.remuneration != null ? societe.gerant.remuneration : "",
          dividendes: societe.gerant?.dividendes != null ? societe.gerant.dividendes : "",
          compteCourantAssocie: societe.gerant?.compteCourantAssocie != null ? societe.gerant.compteCourantAssocie : "",
          pourcentageCapitalDirigeant: societe.gerant?.detentionCapital != null ? societe.gerant.detentionCapital : "",
          pourcentageVotesDirigeant: societe.gerant?.detentionVotes != null ? societe.gerant.detentionVotes : "",
          dateAudit: societe.dateAudit ? new Date(societe.dateAudit).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
          auditeur: user?.Nom || "Non assigné",
        });
      } else {
        setFormData(getInitialState());
      }
      setStep(1);
    }
  }, [societe, user, open]);

  const steps = [
    { id: 1, label: "Identité Juridique", icon: Building2 },
    { id: 2, label: "Finances & Dette", icon: Euro },
    { id: 3, label: "Dirigeants", icon: Users },
    { id: 4, label: "Stratégie & Projets", icon: Target },
    { id: 5, label: "Analyse & Scores", icon: ClipboardCheck },
    { id: 6, label: "Enregistrement", icon: CheckCircle2 },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const payload = {};
      Object.keys(formData).forEach(key => {
        const val = formData[key];
        payload[key] = val === "" ? null : val;
      });

      if (societe?.id) {
        await api.put(`/societes/${societe.id}`, payload);
      } else {
        await api.post(`/societes`, payload);
      }
      showToast("Audit de société enregistré en brouillon avec succès.", "success");
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement :", error);
      showToast(error.response?.data?.message || "Erreur lors de l'enregistrement de l'audit.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }} 
          className="absolute inset-0 bg-ink-900/60 backdrop-blur-sm"
          onClick={onClose}
        />
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="relative bg-surface w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-hp-xl border border-workspace flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-workspace bg-surface-muted/30">
            <div>
              <h2 className="text-xl font-bold text-primary flex items-center gap-2">
                <ClipboardCheck className="w-6 h-6 text-info-500" />
                Audit Entreprise : {formData.denomination || "Nouvelle Société"}
              </h2>
              <p className="text-sm text-subtle mt-1">Saisie complète et structurée du dossier professionnel</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar Steps */}
            <div className="w-64 border-r border-workspace bg-surface-muted/10 p-4 overflow-y-auto hidden md:block">
              <nav className="space-y-1">
                {steps.map((s) => {
                  const Icon = s.icon;
                  const isActive = step === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setStep(s.id)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive 
                          ? "bg-[var(--hp-color-primary)] text-[var(--hp-color-on-primary)] shadow-sm font-semibold" 
                          : "text-subtle hover:bg-workspace hover:text-primary"
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? "text-[var(--hp-color-on-primary)]" : "text-subtle"}`} />
                      {s.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6 max-w-3xl"
                >
                  
                  {step === 1 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><Building2 className="w-5 h-5"/> Identité Juridique</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Dénomination Sociale</label>
                          <Input type="text" name="denomination" value={formData.denomination} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">SIRET</label>
                          <Input type="text" name="siret" value={formData.siret} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Forme Juridique</label>
                          <Input type="text" name="formeJuridique" value={formData.formeJuridique} onChange={handleChange} className=" w-full" placeholder="Ex: SAS, SCI..." />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Date de Création</label>
                          <Input type="date" name="dateCreation" value={formData.dateCreation?.split('T')[0] || ''} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Capital Social (€)</label>
                          <Input type="number" name="capitalSocial" value={formData.capitalSocial} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Secteur d'Activité</label>
                          <Input type="text" name="secteurActivite" value={formData.secteurActivite} onChange={handleChange} className=" w-full" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <input type="checkbox" name="holding" checked={formData.holding} onChange={handleChange} className="h-4 w-4 rounded border-workspace text-primary focus:ring-primary" />
                        <label className="text-sm font-medium text-primary">Est une Holding (tête de pont)</label>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><Euro className="w-5 h-5"/> Données Financières</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Chiffre d'Affaires (€)</label>
                          <Input type="number" name="chiffreAffaires" value={formData.chiffreAffaires} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">EBITDA (€)</label>
                          <Input type="number" name="ebitda" value={formData.ebitda} onChange={handleChange} className=" w-full font-semibold text-success-600" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Résultat Net (€)</label>
                          <Input type="number" name="resultatNet" value={formData.resultatNet} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Trésorerie Disponible (€)</label>
                          <Input type="number" name="tresorerieDisponible" value={formData.tresorerieDisponible} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1 text-danger-500">Dettes & Emprunts (€)</label>
                          <Input type="number" name="dettes" value={formData.dettes} onChange={handleChange} className=" w-full border-danger-200 focus:border-danger-500" />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-info-500 mb-1">Valorisation Estimée (€)</label>
                          <Input type="number" name="valorisationEstimee" value={formData.valorisationEstimee} onChange={handleChange} className=" w-full bg-info-50 dark:bg-info-900/20 font-bold" />
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><Users className="w-5 h-5"/> Dirigeant & Actionnariat</h3>
                      <div className="p-4 bg-surface-muted rounded-xl border border-workspace mb-4">
                        <p className="text-xs text-subtle mb-1">Pour la gestion fine des associés, utilisez le module "Organigramme" dans la fiche client qui gèrera les tables de Participations.</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Rémunération Dirigeant (€)</label>
                          <Input type="number" name="remunerationDirigeant" value={formData.remunerationDirigeant} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Dividendes Versés (€)</label>
                          <Input type="number" name="dividendes" value={formData.dividendes} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Compte Courant d'Associé (€)</label>
                          <Input type="number" name="compteCourantAssocie" value={formData.compteCourantAssocie} onChange={handleChange} className=" w-full" />
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><Target className="w-5 h-5"/> Stratégie & Projets</h3>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center gap-3 p-4 border border-workspace rounded-xl bg-surface-muted/30">
                          <input type="checkbox" name="projetCession" checked={formData.projetCession} onChange={handleChange} className="h-5 w-5 rounded border-workspace text-primary focus:ring-primary" />
                          <label className="text-sm font-medium text-primary">Projet de Cession</label>
                        </div>
                        <div className="flex items-center gap-3 p-4 border border-workspace rounded-xl bg-surface-muted/30">
                          <input type="checkbox" name="transmissionEntreprise" checked={formData.transmissionEntreprise} onChange={handleChange} className="h-5 w-5 rounded border-workspace text-primary focus:ring-primary" />
                          <label className="text-sm font-medium text-primary">Transmission aux enfants</label>
                        </div>
                        <div className="flex items-center gap-3 p-4 border border-workspace rounded-xl bg-surface-muted/30">
                          <input type="checkbox" name="leveeFonds" checked={formData.leveeFonds} onChange={handleChange} className="h-5 w-5 rounded border-workspace text-primary focus:ring-primary" />
                          <label className="text-sm font-medium text-primary">Levée de Fonds</label>
                        </div>
                        <div className="flex items-center gap-3 p-4 border border-workspace rounded-xl bg-surface-muted/30">
                          <input type="checkbox" name="acquisition" checked={formData.acquisition} onChange={handleChange} className="h-5 w-5 rounded border-workspace text-primary focus:ring-primary" />
                          <label className="text-sm font-medium text-primary">Croissance Externe (M&A)</label>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-subtle mb-1">Horizon de Cession (si concerné)</label>
                        <Input type="text" name="horizonCession" value={formData.horizonCession} onChange={handleChange} className=" w-full" placeholder="Ex: 3 à 5 ans" />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Besoin de Financement (€)</label>
                          <Input type="number" name="besoinFinancement" value={formData.besoinFinancement} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Description Projet de Financement</label>
                          <Input type="text" name="projetFinancement" value={formData.projetFinancement} onChange={handleChange} className=" w-full" />
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 5 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><ClipboardCheck className="w-5 h-5"/> Analyse & Scores</h3>
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Score Financement /10</label>
                          <Input type="number" name="scoreFinancement" value={formData.scoreFinancement} onChange={handleChange} max="10" className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Score Transmission /10</label>
                          <Input type="number" name="scoreTransmission" value={formData.scoreTransmission} onChange={handleChange} max="10" className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Score Risque /10</label>
                          <Input type="number" name="scoreRisque" value={formData.scoreRisque} onChange={handleChange} max="10" className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Score Complexité /10</label>
                          <Input type="number" name="scoreComplexite" value={formData.scoreComplexite} onChange={handleChange} max="10" className=" w-full" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-subtle mb-1">Forces de l'entreprise</label>
                        <textarea name="forces" value={formData.forces} onChange={handleChange} className="workspace-input w-full h-20" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-subtle mb-1">Faiblesses / Risques</label>
                        <textarea name="risques" value={formData.risques} onChange={handleChange} className="workspace-input w-full h-20" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-subtle mb-1">Recommandations M&A ou Patrimoniales</label>
                        <textarea name="recommandations" value={formData.recommandations} onChange={handleChange} className="workspace-input w-full h-24 bg-info-50/50 dark:bg-info-900/10 border-info-100 dark:border-info-800" />
                      </div>
                    </div>
                  )}

                  {step === 6 && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-primary-500"/> Enregistrement de l'Audit</h3>
                      
                      <div className="bg-surface-subtle/50 text-subtle p-4 rounded-xl border border-workspace">
                        <p className="font-medium text-sm mb-2">En enregistrant cet audit :</p>
                        <ul className="list-disc list-inside text-sm space-y-1">
                          <li>Les données financières et patrimoniales renseignées seront sauvegardées.</li>
                          <li>Un audit au statut Brouillon (Audit Corporate) sera créé dans l'espace de travail.</li>
                        </ul>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Date de l'Audit</label>
                          <Input type="date" name="dateAudit" value={formData.dateAudit} onChange={handleChange} className=" w-full" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-subtle mb-1">Auditeur Responsable</label>
                          <Input type="text" name="auditeur" value={formData.auditeur} onChange={handleChange} className=" w-full" disabled />
                        </div>
                      </div>

                      <div className="pt-4 flex justify-end gap-3">
                        <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Annuler</Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting} className="gap-2">
                          <CheckCircle2 className="w-5 h-5" />
                          {isSubmitting ? "Enregistrement..." : "Enregistrer le brouillon"}
                        </Button>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Footer Nav */}
          <div className="px-6 py-4 border-t border-workspace bg-surface-muted/30 flex justify-between items-center">
            <Button
              variant="outline"
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1 || isSubmitting}
            >
              Précédent
            </Button>
            <div className="flex gap-1.5">
              {steps.map(s => (
                <div key={s.id} className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${step === s.id ? "bg-[var(--hp-color-primary)] scale-110" : "bg-[var(--hp-color-border-strong)] opacity-50"}`} />
              ))}
            </div>
            {step < steps.length ? (
              <Button variant="primary" onClick={() => setStep(step + 1)} disabled={isSubmitting}>
                Suivant
              </Button>
            ) : (
              <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting} className="gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {isSubmitting ? "Enregistrement..." : "Enregistrer le brouillon"}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
